/*

? Reporting in chat on the website (18 Oct 2026, the app's ReportReasonsSheet): a message, or the
? profile on the other side, with the shared reasons (lib/chat/reportChat.ts REPORT_REASONS) and
? "Also block this profile". The report goes to chat/report; "Also block" then blocks through the
? same route the chat header's Block uses (account-settings/blocked-accounts/block-user), with the
? same reasons, and freezes the thread.

*/

/* Common Imports */

import { useTheme } from "@mui/system";
import React from "react";

/* Redux Imports */

import { setCurrentChatSettingsThunk } from "../../../../redux-magic/store";

/* Component Imports */

import { Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Typography } from "@mui/material";
import BeegruButton from "../../../common-components/buttons/BeegruButton";
import { REPORT_REASONS } from "../../../../lib/chat/reportReasons";
import { BROADCAST_REPORT_REASONS } from "../../../../lib/reports/contentReportReasons";

/** Who "Also block" blocks: the other side, a business by its id, a person by their user id. */
export type ReportBlockTarget = { is_business_profile: boolean; id: string };

const ChatReportDialog = ({
	open,
	onClose,
	dispatch,
	locale,
	profile_context,
	chat_id,
	message_id,
	broadcast_id,
	block_target,
	broadcast_card,
	onDone,
}: {
	open: boolean;
	onClose: () => void;
	dispatch: Function;
	locale?: string;
	profile_context: any;
	/* The chat, or "" for a broadcast with no chat yet (then [broadcast_id]). */
	chat_id: string;
	/* The reported message, or "" to report the profile. */
	message_id: string;
	broadcast_id?: string;
	/* Offered "Also block" when given (this side hasn't blocked them yet). */
	block_target: ReportBlockTarget | null;
	/* Report the broadcast card [broadcast_id] on its own (3 Nov 2026, the app's "Report this broadcast"): its own
	   reasons, "Also block" its sender when given, and the card is then declined for this side. */
	broadcast_card?: boolean;
	/* Called once the report is filed, with whether the profile was blocked too. */
	onDone: (blocked: boolean) => void;
}) => {
	const theme = useTheme();
	const ar: boolean = locale === "ar-AE";
	const kn: boolean = locale === "kn-IN";
	const lang = {
		title_message: ar ? "الإبلاغ عن هذه الرسالة" : kn ? "ಈ ಸಂದೇಶವನ್ನು ವರದಿ ಮಾಡಿ" : "Report this message",
		title_broadcast: ar ? "الإبلاغ عن هذا البث" : kn ? "ಈ ಬ್ರಾಡ್‌ಕಾಸ್ಟ್ ಅನ್ನು ವರದಿ ಮಾಡಿ" : "Report this broadcast",
		title_profile: ar ? "الإبلاغ عن هذا الملف الشخصي" : kn ? "ಈ ಪ್ರೊಫೈಲ್ ಅನ್ನು ವರದಿ ಮಾಡಿ" : "Report this profile",
		body: ar ? "ملاحظاتك تساعدنا في الحفاظ على أمان المجتمع." : kn ? "ನಿಮ್ಮ ಪ್ರತಿಕ್ರಿಯೆ ಸಮುದಾಯವನ್ನು ಸುರಕ್ಷಿತವಾಗಿಡಲು ಸಹಾಯ ಮಾಡುತ್ತದೆ." : "Your feedback helps us keep the community safe.",
		also_block: ar ? "حظر هذا الملف الشخصي أيضًا" : kn ? "ಈ ಪ್ರೊಫೈಲ್ ಅನ್ನು ನಿರ್ಬಂಧಿಸಿ ಕೂಡ" : "Also block this profile",
		cancel: ar ? "إلغاء" : kn ? "ರದ್ದುಮಾಡಿ" : "Cancel",
		report: ar ? "إبلاغ" : kn ? "ವರದಿ ಮಾಡಿ" : "Report",
		error: ar ? "تعذر إرسال البلاغ. يرجى المحاولة مرة أخرى." : kn ? "ವರದಿಯನ್ನು ಕಳುಹಿಸಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ." : "Couldn't send the report. Please try again.",
	};

	const [reasons, setReasons] = React.useState<Array<string>>([]);
	const [alsoBlock, setAlsoBlock] = React.useState<boolean>(false);
	const [sending, setSending] = React.useState<boolean>(false);
	const [failed, setFailed] = React.useState<boolean>(false);

	// A fresh dialog on every opening.
	React.useEffect(() => {
		if (open) {
			setReasons([]);
			setAlsoBlock(false);
			setSending(false);
			setFailed(false);
		}
	}, [open]);

	const submit = async () => {
		if (sending || reasons.length === 0) return;
		setSending(true);
		setFailed(false);
		try {
			const response = await fetch(process.env.PRODUCTION_API_URL + "chat/report", {
				method: "POST",
				cache: "no-store",
				body: JSON.stringify({
					...(chat_id ? { chat_id } : broadcast_id ? { broadcast_id } : {}),
					...(message_id ? { message_id } : {}),
					...(broadcast_card ? { target: "broadcast" } : {}),
					reasons,
					is_business_profile: profile_context?.is_business_profile === true,
					user_id: profile_context?.user_id,
					business_profile_id: profile_context?.business_profile_id,
				}),
			});
			const result = await response.json().catch(() => null);
			if (!response.ok || result?.status !== "success") throw new Error("report");
		} catch {
			setSending(false);
			setFailed(true);
			return;
		}
		let blocked: boolean = false;
		if (alsoBlock && block_target?.id) {
			try {
				const response = await fetch(process.env.PRODUCTION_API_URL + "account-settings/blocked-accounts/block-user", {
					method: "POST",
					cache: "no-store",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						...(block_target.is_business_profile ? { blocked_business_profile_id: block_target.id } : { blocked_user_id: block_target.id }),
						...(profile_context?.is_business_profile === true && profile_context?.business_profile_id
							? { business_profile_id: String(profile_context.business_profile_id) }
							: {}),
						reasons,
					}),
				});
				const result = await response.json().catch(() => null);
				blocked = response.ok && result?.status === "success";
				if (blocked) {
					dispatch(
						setCurrentChatSettingsThunk({
							is_blocked: true,
							my_block_id: typeof result?.payload?.block_id === "string" ? result.payload.block_id : "",
						}),
					);
				}
			} catch {
				blocked = false;
			}
		}
		setSending(false);
		onDone(blocked);
	};

	return (
		<Dialog open={open} onClose={() => (sending ? null : onClose())} fullWidth maxWidth="xs" dir={ar ? "rtl" : "ltr"}>
			<DialogTitle sx={{ fontWeight: 600 }}>{broadcast_card ? lang.title_broadcast : message_id ? lang.title_message : lang.title_profile}</DialogTitle>
			<DialogContent>
				<DialogContentText sx={{ fontSize: "0.875rem", color: theme.palette.text.secondary, marginBottom: "0.5rem" }}>
					{lang.body}
				</DialogContentText>
				{(broadcast_card ? BROADCAST_REPORT_REASONS : REPORT_REASONS).map((reason: string) => (
					<div
						key={reason}
						onClick={() => {
							if (sending) return;
							setReasons((current) => (current.includes(reason) ? current.filter((r) => r !== reason) : [...current, reason]));
						}}
						style={{ display: "flex", alignItems: "center", gap: "0.25rem", cursor: "pointer" }}
					>
						<Checkbox checked={reasons.includes(reason)} disabled={sending} size="small" tabIndex={-1} />
						<Typography variant="body2">{reason}</Typography>
					</div>
				))}
				{block_target ? (
					<div
						onClick={() => (sending ? null : setAlsoBlock((value) => !value))}
						style={{ display: "flex", alignItems: "center", gap: "0.25rem", cursor: "pointer", marginTop: "0.5rem" }}
					>
						<Checkbox checked={alsoBlock} disabled={sending} size="small" tabIndex={-1} />
						<Typography variant="body2" sx={{ fontWeight: 600 }}>
							{lang.also_block}
						</Typography>
					</div>
				) : null}
				{failed ? (
					<Typography variant="body2" role="alert" sx={{ fontSize: "0.8125rem", color: theme.palette.error.main, marginTop: "0.75rem" }}>
						{lang.error}
					</Typography>
				) : null}
			</DialogContent>
			<DialogActions sx={{ padding: "1rem" }}>
				<BeegruButton variant="outlined" disabled={sending} onClick={onClose} sx={{ textTransform: "none", color: theme.palette.text.secondary }}>
					{lang.cancel}
				</BeegruButton>
				<BeegruButton
					onClick={submit}
					variant="contained"
					flavor="error"
					disabled={reasons.length === 0}
					loading={sending}
					sx={{ textTransform: "none", borderRadius: "0.5rem" }}
				>
					{lang.report}
				</BeegruButton>
			</DialogActions>
		</Dialog>
	);
};

export default ChatReportDialog;
